import { defineComponent as _defineComponent } from 'vue'
import { openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, createTextVNode as _createTextVNode, createElementBlock as _createElementBlock, Fragment as _Fragment, renderList as _renderList, normalizeClass as _normalizeClass, unref as _unref, createVNode as _createVNode } from "vue"

const _hoisted_1 = { class: "about" }
const _hoisted_2 = { key: 0 }
const _hoisted_3 = {
  href: "https://github.com/epinio/epinio",
  target: "_blank",
  rel: "nofollow noopener noreferrer"
}
const _hoisted_4 = { class: "pt-40" }
const _hoisted_5 = { class: "os" }
const _hoisted_6 = ["href"]
const _hoisted_7 = ["href"]
const _hoisted_8 = {
  key: 4,
  class: "download-report"
}
const _hoisted_9 = { class: "download-report__actions" }
const _hoisted_10 = ["disabled"]
const _hoisted_11 = {
  key: 0,
  class: "icon-spinner animate-spin mr-5"
}
const _hoisted_12 = {
  key: 5,
  class: "support-bundle"
}
const _hoisted_13 = { class: "text-muted" }
const _hoisted_14 = { class: "support-bundle__controls" }
const _hoisted_15 = { class: "support-bundle__actions" }
const _hoisted_16 = ["disabled"]
const _hoisted_17 = {
  key: 0,
  class: "icon-spinner animate-spin mr-5"
}
const _hoisted_18 = {
  key: 0,
  class: "support-bundle__progress"
}

import { onMounted, ref, computed } from 'vue';
import { useStore } from 'vuex';
import Banner from '@components/Banner/Banner.vue';
import Checkbox from '@components/Form/Checkbox/Checkbox.vue';
import LabeledInput from '@components/Form/LabeledInput/LabeledInput.vue';
import { MANAGEMENT } from '@shell/config/types';
import { getVendor } from '@shell/config/private-label';
import { downloadFile } from '@shell/utils/download';

const downloadLinuxImages = null;


export default /*@__PURE__*/_defineComponent({
  __name: 'about',
  setup(__props) {

const store = useStore();

const version = ref<any>(null);
const settings = ref<any[]>([]);
const fetchError = ref<string>('');
const tailLines = ref<number>(1000);
const includeApps = ref<boolean>(false);
const supportBundleLoading = ref(false);
const supportBundleError = ref('');
const supportBundleSuccess = ref('');

const reportLoading = ref(false);
const reportError = ref('');
const reportSuccess = ref('');

const t = store.getters['i18n/t'];

const aboutVersionsComponentString = computed(() => t('about.versions.component'));
const aboutTitleString = computed(() => t('about.title'));
const aboutVersionsVersionString = computed(() => t('about.versions.version'));
const aboutDownloadCLIString = computed(() => t('about.downloadCLI.title'));
const allPackagesString = computed(() => t('epinio.about.allPackages'));


const fetchData = async() => {
  fetchError.value = '';

  try {
    settings.value = await store.dispatch(`management/findAll`, { type: MANAGEMENT.SETTING });
  } catch (err: any) {
    console.warn('Failed to load settings on About page', err);
  }

  try {
    version.value = await store.dispatch('epinio/version');
  } catch (err: any) {
    console.warn('Failed to load version on About page', err);
    fetchError.value = t('epinio.supportBundle.errors.failed');
  }
};

onMounted(fetchData);

const appName = computed(() => {
  const isSingleProduct = !!store.getters['isSingleProduct'];
  return isSingleProduct ? getVendor() : t('epinio.label');
});

function createOSOption(label: string, icon: string, cliLink: string, imageList: any = null) {
  const slash = cliLink?.lastIndexOf('/');
  return {
    label,
    icon,
    imageList,
    cliLink,
    cliFile: slash >= 0 ? cliLink.substr(slash + 1) : cliLink
  };
}

const downloads = computed(() => {
  if (!version.value) {
    return [];
  }

  const gitUrl = `https://github.com/epinio/epinio/releases/download`;
  const versionStr = version.value.displayVersion;
  const app = appName.value.toLowerCase();

  return [
    createOSOption('about.os.mac', 'icon-apple', `${gitUrl}/${versionStr}/${app}-darwin-x86_64`),
    createOSOption('about.os.linux', 'icon-linux', `${gitUrl}/${versionStr}/${app}-linux-x86_64`, downloadLinuxImages),
    createOSOption('about.os.windows', 'icon-windows', `${gitUrl}/${versionStr}/${app}-windows-x86_64.zip`)
  ];
});

const versionString = computed(() => {
  if (!version.value) return '';
  if (version.value.displayVersion === version.value.fullVersion) {
    return version.value.displayVersion;
  }
  return version.value.fullVersion;
});

const sanitizeTail = (value: number | string) => {
  const parsed = Number(value);

  if (!Number.isFinite(parsed)) {
    return 1000;
  }

  return Math.max(1, Math.min(Math.round(parsed), 10000));
};

const bundleFileName = () => {
  const now = new Date();
  const pad = (val: number) => val.toString().padStart(2, '0');

  return `epinio-support-bundle-${ now.getFullYear() }-${ pad(now.getMonth() + 1) }-${ pad(now.getDate()) }-${ pad(now.getHours()) }-${ pad(now.getMinutes()) }-${ pad(now.getSeconds()) }.tar.gz`;
};

const reportFileName = () => {
  const now = new Date();
  const pad = (val: number) => val.toString().padStart(2, '0');

  return `epinio-report-${ now.getFullYear() }-${ pad(now.getMonth() + 1) }-${ pad(now.getDate()) }-${ pad(now.getHours()) }-${ pad(now.getMinutes()) }-${ pad(now.getSeconds()) }.txt`;
};

const downloadReport = async() => {
  reportError.value = '';
  reportSuccess.value = '';
  reportLoading.value = true;

  try {
    const res = await store.dispatch('epinio/request', {
      opt: {
        url:          '/api/v1/report/nodes',
        method:       'get',
        params:       { format: 'text' },
        responseType: 'blob',
        timeout:      60000
      }
    });

    const blob = res?.data;
    const contentType = res?.headers?.['content-type'] || 'text/plain; charset=utf-8';

    await downloadFile(reportFileName(), blob, contentType);

    reportSuccess.value = t('epinio.downloadReport.success');
  } catch (err: any) {
    const status = err?._status || err?.status || err?.response?.status;

    reportError.value = status === 403
      ? t('epinio.downloadReport.errors.unauthorized')
      : t('epinio.downloadReport.errors.failed');
  } finally {
    reportLoading.value = false;
  }
};

const downloadSupportBundle = async() => {
  const safeTail = sanitizeTail(tailLines.value);

  tailLines.value = safeTail;
  supportBundleError.value = '';
  supportBundleSuccess.value = '';
  supportBundleLoading.value = true;

  try {
    const res = await store.dispatch('epinio/request', {
      opt: {
        url:          '/api/v1/support-bundle',
        method:       'get',
        params:       { tail: safeTail, include_apps: includeApps.value },
        responseType: 'blob',
        timeout:      600000
      }
    });

    const blob = res?.data;
    const contentType = res?.headers?.['content-type'] || 'application/gzip';

    await downloadFile(bundleFileName(), blob, contentType);

    supportBundleSuccess.value = t('epinio.supportBundle.success');
  } catch (err: any) {
    const status = err?._status || err?.status || err?.response?.status;

    supportBundleError.value = status === 403
      ? t('epinio.supportBundle.errors.unauthorized')
      : t('epinio.supportBundle.errors.failed');
  } finally {
    supportBundleLoading.value = false;
  }
};


return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    (fetchError.value)
      ? (_openBlock(), _createBlock(Banner, {
          key: 0,
          color: "error",
          label: fetchError.value,
          class: "mb-20"
        }, null, 8, ["label"]))
      : _createCommentVNode("", true),
    _cache[18] || (_cache[18] = _createTextVNode()),
    (version.value)
      ? (_openBlock(), _createElementBlock(_Fragment, { key: 1 }, [
          _createElementVNode("h1", null, _toDisplayString(aboutTitleString.value), 1),
          _cache[5] || (_cache[5] = _createTextVNode()),
          _createElementVNode("table", null, [
            _createElementVNode("thead", null, [
              _createElementVNode("tr", null, [
                _createElementVNode("th", null, _toDisplayString(aboutVersionsComponentString.value), 1),
                _cache[2] || (_cache[2] = _createTextVNode()),
                _createElementVNode("th", null, _toDisplayString(aboutVersionsVersionString.value), 1)
              ])
            ]),
            _cache[4] || (_cache[4] = _createTextVNode()),
            (version.value)
              ? (_openBlock(), _createElementBlock("tr", _hoisted_2, [
                  _createElementVNode("td", null, [
                    _createElementVNode("a", _hoisted_3, _toDisplayString(appName.value), 1)
                  ]),
                  _cache[3] || (_cache[3] = _createTextVNode()),
                  _createElementVNode("td", null, _toDisplayString(versionString.value), 1)
                ]))
              : _createCommentVNode("", true)
          ])
        ], 64))
      : _createCommentVNode("", true),
    _cache[19] || (_cache[19] = _createTextVNode()),
    (version.value && downloads.value.length)
      ? (_openBlock(), _createElementBlock(_Fragment, { key: 2 }, [
          _createElementVNode("h3", _hoisted_4, _toDisplayString(aboutDownloadCLIString.value), 1),
          _cache[7] || (_cache[7] = _createTextVNode()),
          _createElementVNode("table", null, [
            (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(downloads.value, (d) => {
              return (_openBlock(), _createElementBlock("tr", {
                key: d.icon,
                class: "link"
              }, [
                _createElementVNode("td", null, [
                  _createElementVNode("div", _hoisted_5, [
                    _createElementVNode("i", {
                      class: _normalizeClass(`icon ${d.icon} mr-5`)
                    }, null, 2),
                    _createTextVNode(" " + _toDisplayString(_unref(t)(d.label)), 1)
                  ])
                ]),
                _cache[6] || (_cache[6] = _createTextVNode()),
                _createElementVNode("td", null, [
                  (d.cliLink)
                    ? (_openBlock(), _createElementBlock("a", {
                        key: 0,
                        href: d.cliLink
                      }, _toDisplayString(d.cliFile), 9, _hoisted_6))
                    : _createCommentVNode("", true)
                ])
              ]))
            }), 128))
          ])
        ], 64))
      : _createCommentVNode("", true),
    _cache[20] || (_cache[20] = _createTextVNode()),
    (version.value)
      ? (_openBlock(), _createElementBlock("a", {
          key: 3,
          class: "mt-5",
          target: "_blank",
          href: `https://github.com/epinio/epinio/releases/tag/${version.value.displayVersion}`
        }, _toDisplayString(allPackagesString.value), 9, _hoisted_7))
      : _createCommentVNode("", true),
    _cache[21] || (_cache[21] = _createTextVNode()),
    (version.value)
      ? (_openBlock(), _createElementBlock("section", _hoisted_8, [
          _createElementVNode("h3", null, _toDisplayString(_unref(t)('epinio.downloadReport.title')), 1),
          _cache[8] || (_cache[8] = _createTextVNode()),
          _createElementVNode("div", _hoisted_9, [
            _createElementVNode("button", {
              class: "btn role-primary",
              disabled: reportLoading.value,
              onClick: downloadReport
            }, [
              (reportLoading.value)
                ? (_openBlock(), _createElementBlock("i", _hoisted_11))
                : _createCommentVNode("", true),
              _createTextVNode(" " + _toDisplayString(_unref(t)('epinio.downloadReport.action')), 1)
            ], 8, _hoisted_10)
          ]),
          _cache[9] || (_cache[9] = _createTextVNode()),
          (reportSuccess.value)
            ? (_openBlock(), _createBlock(Banner, {
                key: 0,
                color: "success",
                label: reportSuccess.value
              }, null, 8, ["label"]))
            : _createCommentVNode("", true),
          _cache[10] || (_cache[10] = _createTextVNode()),
          (reportError.value)
            ? (_openBlock(), _createBlock(Banner, {
                key: 1,
                color: "error",
                label: reportError.value
              }, null, 8, ["label"]))
            : _createCommentVNode("", true)
        ]))
      : _createCommentVNode("", true),
    _cache[22] || (_cache[22] = _createTextVNode()),
    (version.value)
      ? (_openBlock(), _createElementBlock("section", _hoisted_12, [
          _createElementVNode("h3", null, _toDisplayString(_unref(t)('epinio.supportBundle.title')), 1),
          _cache[13] || (_cache[13] = _createTextVNode()),
          _createElementVNode("p", _hoisted_13, _toDisplayString(_unref(t)('epinio.supportBundle.description')), 1),
          _cache[14] || (_cache[14] = _createTextVNode()),
          _createElementVNode("div", _hoisted_14, [
            _createVNode(LabeledInput, {
              value: tailLines.value,
              "onUpdate:value": _cache[0] || (_cache[0] = ($event: any) => ((tailLines).value = $event)),
              type: "number",
              label: _unref(t)('epinio.supportBundle.tail.label'),
              tooltip: _unref(t)('epinio.supportBundle.tail.help'),
              min: 1,
              max: 10000,
              disabled: supportBundleLoading.value
            }, null, 8, ["value", "label", "tooltip", "disabled"]),
            _cache[11] || (_cache[11] = _createTextVNode()),
            _createVNode(Checkbox, {
              value: includeApps.value,
              "onUpdate:value": _cache[1] || (_cache[1] = ($event: any) => ((includeApps).value = $event)),
              label: _unref(t)('epinio.supportBundle.includeApps'),
              disabled: supportBundleLoading.value
            }, null, 8, ["value", "label", "disabled"])
          ]),
          _cache[15] || (_cache[15] = _createTextVNode()),
          _createElementVNode("div", _hoisted_15, [
            _createElementVNode("button", {
              class: "btn role-primary",
              disabled: supportBundleLoading.value,
              onClick: downloadSupportBundle
            }, [
              (supportBundleLoading.value)
                ? (_openBlock(), _createElementBlock("i", _hoisted_17))
                : _createCommentVNode("", true),
              _createTextVNode(" " + _toDisplayString(_unref(t)('epinio.supportBundle.action')), 1)
            ], 8, _hoisted_16),
            _cache[12] || (_cache[12] = _createTextVNode()),
            (supportBundleLoading.value)
              ? (_openBlock(), _createElementBlock("span", _hoisted_18, _toDisplayString(_unref(t)('epinio.supportBundle.collecting')), 1))
              : _createCommentVNode("", true)
          ]),
          _cache[16] || (_cache[16] = _createTextVNode()),
          (supportBundleSuccess.value)
            ? (_openBlock(), _createBlock(Banner, {
                key: 0,
                color: "success",
                label: supportBundleSuccess.value
              }, null, 8, ["label"]))
            : _createCommentVNode("", true),
          _cache[17] || (_cache[17] = _createTextVNode()),
          (supportBundleError.value)
            ? (_openBlock(), _createBlock(Banner, {
                key: 1,
                color: "error",
                label: supportBundleError.value
              }, null, 8, ["label"]))
            : _createCommentVNode("", true)
        ]))
      : _createCommentVNode("", true)
  ]))
}
}

})